// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartHeader.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Report part header component
 */

import { action } from 'mobx';
import classnames from 'classnames';
import constants from 'logic/data/Izenda.ReportBodyConstants';
import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import Validator from 'share/components/hoc/Izenda.Validator';
import Input from 'share/components/controls/Izenda.Input';
const ValidatableInput = Validator({ tooltipPosition: 'bottom' })(Input);
import EventsUtils from 'EventUtils';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import { isNil, forEach, merge, invokeArgs, invoke } from 'lodash/fp';
const notifyChange = EventsUtils.notifyChange;

import reportPartTypeManager from 'Izenda.ReportPartTypeManager';
import reportPartHeaderAction from './Izenda.ReportPartHeaderActions';
import React, { Component } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import MenuContext from 'share/components/controls/Izenda.MenuContext';
import UiState from 'logic/Izenda.UiState';
import AppGlobal from 'Izenda.AppGlobal';

@connect(reportPartHeaderAction)
export default class ReportPartHeader extends Component {
  static getDefaultProps = {
    viewMode: false, // Use for view mode only in Report Viewer
    quickEdit: false
  };

  constructor(props) {
    super(props);
    this.state = {
      editMode: false,
      inputTitle: props.reportPart.headerTitle,
      isBackSide: props.reportPart.isBackSide
    };
  }

  onBlurNumberOfRecord = (event: any): void => {
    if (this.isChangedNumberOfRecord) {
      this.isChangedNumberOfRecord = false;

      const { store, validation, reportPart } = this.props;
      validation.validate('record').then(success => {
        if (success) {
          AppGlobal.canShowPreview() && store.needUpdatePreview(reportPart);
        }
      });
    }
  };

  onKeyDownNumberOfRecords = (event: any): void => {
    if (event.keyCode === constants.EVENT_ENTER_CHANGE_NUMBERS_OF_RECORDS) {
      this.onBlurNumberOfRecord();
    }
  };

  /**
   * Change number of record
   * @param {string} value input number from UI
   * @return {any}
   */
  onChangeNumberOfRecord = (value: string): void => {
    this.isChangedNumberOfRecord = true;
    this.props.reportPart.setNumberOfRecord(value);
    if (!AppGlobal.canShowPreview() && !this.props.reportPart.isBackSide) {
      this.props.loadReportPartData();
    }
  };

  /**
   * Get report part config
   * @return {any} Report part config
   */
  getReportPartConfig = (): any => {
    return reportPartTypeManager.getReportPartType(this.props.reportPart.type);
  };

  /**
   * switch to edit report name mode for front side
   * @return {[type]} [description]
   */
  editReportName = (): void => {
    const { localization, reportPart } = this.props;
    ensure(!reportPart.isBackSide, localization.getText('REPORTDESIGNER_MESSAGE_MUST_BE_FRONT_SIDE_TO_RUN'));
    this.setState({ editMode: true, inputTitle: reportPart.headerTitle });
  };

  /**
   * reset state after flip side
   */
  componentWillReceiveProps = (nextProps: any) => {
    const { isBackSide } = this.state;
    const { reportPart } = this.props;
    if (isBackSide !== reportPart.isBackSide || reportPart !== nextProps.reportPart) {
      this.setState({
        editMode: false,
        inputTitle: nextProps.reportPart.headerTitle,
        isBackSide: nextProps.reportPart.isBackSide
      });
    }
  };

  /**
   * delete current report part
   */
  deleteReportPart = (): void => {
    const { reportPart, reportBody, store } = this.props;

    this.props
      .confirmFn('Information', {
        description: String.format(constants.MESSAGES.M01, reportPart.headerTitle)
      })
      .then(
        action(function() {
          // Delete filter using aggregated field in this report part
          forEach(function(field) {
            store.reportFilters.removeAggregatedField(field);
          }, reportPart.fieldCollection);
          reportBody.deleteReportPart(reportPart);
          if (store) {
            invokeArgs('loadReportPartDataByCrossFiltering', [reportPart.id], store);
            invoke('deletePreviousPreviewData', store);
          }
        })
      );
  };

  /**
   * rename report part name
   */
  renameReportPart = (): void => {
    const { reportPart, reportBody, store } = this.props;
    const state = this.state;

    this.props.validation.validate('inputTitle').then(
      action(success => {
        if (success) {
          const defaultName = this.getReportPartConfig().label;
          store.reportFilters.updateAggregatedFieldReportPartTitle(reportPart.headerTitle, state.inputTitle);
          reportBody.renameReportPart(reportPart, state.inputTitle, defaultName);
          if (!reportPart.isBackSide) {
            this.setState({ editMode: false });
          } else {
            this.setState({ inputTitle: reportPart.headerTitle });
          }
        }
      })
    );
  };

  /**
   * cancel edit name
   */
  cancelEditName = (): void => {
    const { localization, reportPart } = this.props;
    ensure(!reportPart.isBackSide, localization.getText('REPORTDESIGNER_MESSAGE_MUST_BE_FRONT_SIDE_TO_RUN'));
    this.setState({ editMode: false, inputTitle: reportPart.headerTitle });
  };
  /**
   * handle change side behaviour
   */
  changeSide = (): void => {
    const props = this.props;
    const { reportPart, reportBody, store: { reportFilters } } = this.props;
    this.props.validation.validate().then(
      action(function(success) {
        if (success) {
          // Reset the info to update result as change side update result all the parts
          reportFilters.resetUpdateResult();
          if (props.onClick) {
            // Show report part properties panel after that change side of report part
            props.onClick().then(() => {
              reportBody.changeReportPartSide(reportPart);
            });
          } else {
            reportBody.changeReportPartSide(reportPart);
          }
          if (!AppGlobal.canShowPreview() && reportBody.isFullScreen && reportPart.isBackSide) {
            props.loadReportPartData();
          }
        }
      })
    );
  };

  @action
  handleCopyReportPart = () => {
    const { reportPart, reportBody, store } = this.props;
    const newReportPart = reportBody.copyReportPart(reportPart);

    // Get data for new report part
    store.needUpdatePreview(newReportPart);
  };

  render() {
    const { reportPart, viewMode, selectReportPart, onMouseLeave } = this.props;
    const isSelectedReportPartType = !isNil(reportPart.type);
    const className = classnames('report-part-header draggableBox', this.props.className);

    return !viewMode ? (
      <div
        className={className}
        aria-selected={isSelectedReportPartType}
        onMouseDown={selectReportPart}
        onMouseLeave={onMouseLeave}
        onTouchStart={selectReportPart}
      >
        {isSelectedReportPartType ? this.renderSelectedReportHeader() : this.renderUnselectedReportHeader()}
      </div>
    ) : null;
  }

  renderUnselectedReportHeader() {
    const { localization } = this.props;
    const configClassName = classnames('header-control u_paddingAll text-right', {
      hide: !this.props.isShowHeaderConfig && !UiState.isMobile
    });
    return (
      <div className="full-height">
        {this.renderBlankHeader()}
        <div className={configClassName}>
          <a
            className="btn btn-default"
            title={localization.getText('COMMON_LABEL_DELETE')}
            data-btnName="btnAction"
            onClick={this.props.hasPrivileges && this.deleteReportPart}
          >
            <i className="icon-cancel" />
          </a>
        </div>
      </div>
    );
  }

  renderSelectedReportHeader() {
    const { reportPart, viewMode } = this.props;
    let flipIcon = {};
    reportPart.isBackSide
      ? ((flipIcon.className = 'icon-flip-front'), (flipIcon.tooltip = constants.SWITCH_TO_PREVIEW))
      : ((flipIcon.className = 'icon-flip-back'), (flipIcon.tooltip = constants.SWITCH_TO_CONFIGURATION));

    return !viewMode ? this.renderDesignMode() : this.renderViewMode();
  }

  renderViewMode() {
    const configClassName = classnames('izenda-ReportPartHeader-content', {
      hide: !this.props.isShowHeaderConfig && !UiState.isMobile
    });
    return (
      <div className="full-height">
        {this.renderBlankHeader()}
        <div className={configClassName}>
          <div className="col-md-6 title">
            <i className={this.getReportPartConfig().icon} />
            <div className="control-group inline-block">
              <label className="control-label">{this.renderTitle()}</label>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderBlankHeader() {
    const blankClassName = classnames('u_noMarginAll full-height', {
      hide: this.props.isShowHeaderConfig
    });
    return <div className={blankClassName} onMouseEnter={this.props.onMouseEnter} />;
  }

  renderDesignMode() {
    const props = this.props;
    return (
      <div className="full-height">
        {this.renderConfigHeader()}
        {!props.isShowHeaderConfig
          ? props.reportPart.isBackSide
            ? this.renderBackSideHeader()
            : this.renderBlankHeader()
          : null}
      </div>
    );
  }

  /**
   * render backside header
   */
  renderBackSideHeader() {
    const { reportPart, stateReview, localization } = this.props;
    const cols = stateReview !== constants.DIRECTION_STATE.NORMAL || !AppGlobal.canShowPreview() ? 12 : 6;
    const isForm = reportPart.type === REPORT_PART_TYPE.Form;
    return (
      !UiState.isMobile && (
        <GridLayout className="report-part-backside-header u_noMarginAll no-gutter">
          <GridLayout.Row>
            {isForm && this.renderTitleOnly(this.props, reportPart.headerTitle)}
            {!isForm &&
              this.renderResizeSection(
                cols,
                localization.getText('COMMON_LABEL_CONFIGURATION'),
                constants.DIRECTION.LEFT,
                constants.DIRECTION_STATE.RIGHT
              )}
            {!isForm &&
              AppGlobal.canShowPreview() &&
              this.renderResizeSection(
                cols,
                localization.getText('COMMON_LABEL_PREVIEW'),
                constants.DIRECTION.RIGHT,
                constants.DIRECTION_STATE.LEFT
              )}
          </GridLayout.Row>
        </GridLayout>
      )
    );
  }

  renderTitleOnly(props: any, title: any) {
    return (
      <div className="u_paddingLeft" onMouseEnter={props.onMouseEnter}>
        {title}
      </div>
    );
  }

  renderContextMenu() {
    const props = this.props;
    const reportPartType = props.reportPart.type;
    return (
      <div className="izenda-ReportPartHeader_ContextMenu">
        <ul className="izenda-ContextMenu-list">
          <li> {this.renderCopyIcon(true)} </li>
          <li> {this.renderFullScreenIcon(true)} </li>
          <li> {this.renderFlipIcon(true)} </li>
          <li className="izenda-ContextMenu-divider" />
          <li> {this.renderDeleteIcon(true)} </li>
        </ul>
      </div>
    );
  }

  /**
   * render Config header
   */
  renderConfigHeader() {
    const { reportPart, reportBody, viewMode, quickEdit, localization, isShowHeaderConfig, hasPrivileges } = this.props;
    const content = reportPart.reportPartContent;
    const notifyChange = EventsUtils.notifyChange;

    const className = classnames({
      'izenda-ReportPartHeader-content': true,
      'small-size': reportPart.width < 6 && !reportPart.isBackSide && !reportBody.isFullScreen,
      hide: !isShowHeaderConfig && !UiState.isMobile
    });
    return (
      <MenuContext
        id={reportPart.id || new Date().toISOString()}
        menuComponent={this.renderContextMenu()}
        isDisabled={viewMode && quickEdit}
      >
        <div className={className}>
          <div className="izenda-ReportPartHeader-buttonAction">
            {this.renderCopyIcon()}
            {this.renderFullScreenIcon()}
            {this.renderFlipIcon()}
            {this.renderDeleteIcon()}
          </div>

          <div className="izenda-ReportPartHeader-title">
            <i className={this.getReportPartConfig().icon} />
            <div className="control-group inline-block">
              <label className="control-label">
                {this.renderTitle()}
                {hasPrivileges && this.renderTitleEditIcon()}
              </label>
            </div>
          </div>

          {!UiState.isMobile && (
            <div className="izenda-ReportPartHeader-record">
              <label htmlFor="record">{localization.getText('REPORTDESIGNER_LABEL_NUMBER_OF_RECORDS')} </label>
              <ValidatableInput
                className="inline-block form-control"
                validations={constants.NUMBER_OF_RECORD_LIMIT_CONSTRAINT}
                field="record"
                value={reportPart.numberOfRecord}
                onChange={notifyChange(this.onChangeNumberOfRecord, EventsUtils.INPUT)}
                onBlur={this.onBlurNumberOfRecord}
                onKeyDown={this.onKeyDownNumberOfRecords}
                readOnly={!hasPrivileges}
              />
            </div>
          )}

          <div className="u_clearFix u_noMarginAll" />
        </div>
      </MenuContext>
    );
  }

  /**
   * Render flip icon
   * @param {boolean} isContextMenu
   *
   */

  renderFlipIcon(isContextMenu: boolean) {
    const props = this.props;
    let flipIcon = {};
    props.reportPart.isBackSide
      ? ((flipIcon.className = 'icon-flip-front'), (flipIcon.tooltip = constants.SWITCH_TO_PREVIEW))
      : ((flipIcon.className = 'icon-flip-back'), (flipIcon.tooltip = constants.SWITCH_TO_CONFIGURATION));
    return (
      props.hasPermission(null) && (
        <a
          className={!isContextMenu ? 'btn btn-default' : 'btn'}
          title={flipIcon.tooltip}
          data-btnName="btnAction"
          onClick={props.hasPrivileges && this.changeSide}
        >
          <i className={flipIcon.className} />
          {isContextMenu && <span>{flipIcon.tooltip}</span>}
        </a>
      )
    );
  }

  /**
   * Render delete icon
   * @param {boolean} isContextMenu
   *
   */

  renderDeleteIcon(isContextMenu: boolean) {
    const { quickEdit, hasPermission, localization, hasPrivileges } = this.props;
    return (
      !quickEdit &&
      hasPermission(null) && (
        <a
          className={!isContextMenu ? 'btn btn-default' : 'btn'}
          title={localization.getText('COMMON_LABEL_DELETE')}
          data-btnName="btnAction"
          onClick={hasPrivileges && this.deleteReportPart}
        >
          <i className="icon-cancel" />
          {isContextMenu && <span>{localization.getText('COMMON_LABEL_DELETE')}</span>}
        </a>
      )
    );
  }

  /**
   * Render copy icon
   * @param {boolean} isContextMenu
   *
   */

  renderCopyIcon(isContextMenu: boolean) {
    const { reportPart, quickEdit, hasPermission, localization, hasPrivileges } = this.props;
    return (
      !quickEdit &&
      !reportPart.isBackSide &&
      hasPermission(null) && (
        <a
          className={!isContextMenu ? 'btn btn-default' : 'btn'}
          title={localization.getText('COMMON_LABEL_COPY')}
          data-btnName="btnAction"
          onClick={hasPrivileges && this.handleCopyReportPart}
        >
          <i className="icon-copy" />
          {isContextMenu && <span>{localization.getText('COMMON_LABEL_COPY')}</span>}
        </a>
      )
    );
  }

  /**
   * Render icon full screen
   * @return {[type]}
   */
  renderFullScreenIcon(isContextMenu: boolean) {
    const { quickEdit, localization, reportBody } = this.props;
    const iconConfig = reportBody.isFullScreen
      ? {
          title: localization.getText('DASHBOARD_TITLE_NORMAL_SCREEN'),
          className: 'icon-nofullscreen'
        }
      : {
          title: localization.getText('DASHBOARD_TITLE_FULL_SCREEN'),
          className: 'icon-fullscreen'
        };

    return (
      !quickEdit && (
        <a
          className={!isContextMenu ? 'btn btn-default' : 'btn'}
          title={iconConfig.title}
          data-btnName="btnAction"
          onClick={this.handleShowFullScreen}
        >
          <i className={iconConfig.className} />
          {isContextMenu && <span>{iconConfig.title}</span>}
        </a>
      )
    );
  }

  @action
  handleShowFullScreen = () => {
    const props = this.props;
    props.onChangeFullScreen && props.onChangeFullScreen(props.reportPart);
    // Show report part properties editor
    props.onClick && props.onClick();
  };

  /**
   * renser title icon for front side
   * @return {any} Title edit icon
   */
  renderTitleEditIcon() {
    const { reportPart, localization, hasPrivileges, viewMode } = this.props;
    const editReportFn = viewMode ? null : this.editReportName;

    if (reportPart.isBackSide) {
      return null;
    } else if (!this.state.editMode) {
      return (
        <a
          className="btn btn-default"
          data-btnName="btnAction"
          title={localization.getText('REPORTDESIGNER_LABEL_EDIT_REPORT_PART_NAME')}
          onClick={hasPrivileges && editReportFn}
        >
          <i className="icon-pencil-new" />
        </a>
      );
    } else {
      return (
        <span>
          <a
            className="btn btn-default u_noPaddingAll u_marginLeftInnerSpace"
            data-btnName="btnAction"
            title={localization.getText('REPORTDESIGNER_LABEL_SET_NEW_REPORT_PART_NAME')}
            onClick={this.renameReportPart}
          >
            <i className="icon-ok success-color" />
          </a>
          <a
            className="btn btn-default u_noPaddingAll u_marginLeftInnerSpace"
            data-btnName="btnAction"
            title={localization.getText('REPORTDESIGNER_LABEL_REVERT_REPORT_PART_NAME')}
            onClick={this.cancelEditName}
          >
            <i className="icon-cancel" />
          </a>
        </span>
      );
    }
  }

  /**
   * render title
   * @return {any} title
   */
  renderTitle() {
    const { reportPart, reportBody, hasPrivileges } = this.props;
    const state = this.state;
    if (!reportPart.isBackSide && !this.state.editMode) {
      const truncateText = String.truncateString(reportPart.headerTitle, 10);
      return <span title={reportPart.headerTitle}>{truncateText}</span>;
    } else {
      const updateInputTitle = function(newName) {
        this.setState({ inputTitle: newName });
      };
      const onBlurInputField = function() {
        reportPart.isBackSide && this.renameReportPart();
      };
      const inputTitleValidation = merge(
        constants.CONSTRAINTS.NAME,
        {
          action: function() {
            return reportBody.validateNewNameReportPart.bind(reportBody, reportPart, state.inputTitle);
          }
        },
        {}
      );
      return (
        <ValidatableInput
          className="form-control"
          containerClassName="inline-block"
          validations={inputTitleValidation}
          field="inputTitle"
          value={state.inputTitle}
          onChange={notifyChange(updateInputTitle.bind(this), EventsUtils.INPUT)}
          onBlur={onBlurInputField.bind(this)}
          readOnly={!hasPrivileges}
          maxLength={30}
        />
      );
    }
  }

  /**
   * render resize (LEFT/RIGHT) section
   * @param {number} cols: number of columns
   * @param {string} title
   * @param {string} direction
   * @param {string} title
   */
  renderResizeSection(cols: any, title: any, direction: any, iconState: any) {
    const { stateReview } = this.props;

    const className = classnames('u_paddingLeft u_paddingRight', {
      hide: stateReview === iconState || this.props.isShowHeaderConfig,
      'child-panel-expanded': stateReview === -iconState
    });
    const titleClassName = classnames('header-panel text-center', {
      'pull-left': direction === constants.DIRECTION.LEFT,
      'pull-right': direction === constants.DIRECTION.RIGHT
    });
    const iconClassName = classnames('header-panel u_paddingLeft u_paddingRight', {
      'pull-right': direction === constants.DIRECTION.LEFT,
      'pull-left': direction === constants.DIRECTION.RIGHT
    });
    const showPreview = AppGlobal.canShowPreview();
    const titleCols = showPreview ? constants.TITLES_COLS_SHOW_PREVIEW : constants.TITLES_COLS_NOT_SHOW_PREVIEW;

    return (
      <GridLayout.Col cols={cols} className={className}>
        <GridLayout>
          <GridLayout.Row>
            <GridLayout.Col cols={titleCols} className={titleClassName} onMouseEnter={this.props.onMouseEnter}>
              {title}
            </GridLayout.Col>
            {showPreview && (
              <GridLayout.Col cols={1} className={iconClassName}>
                {this.renderExpandIcon(direction)}
              </GridLayout.Col>
            )}
          </GridLayout.Row>
        </GridLayout>
      </GridLayout.Col>
    );
  }

  /**
   * render expand icon
   * @param {string} direction
   */
  renderExpandIcon(direction: any) {
    const { stateReview, reportPart, store, setStateReview } = this.props;
    const showPreview = AppGlobal.canShowPreview();

    let icon = '';
    if (direction === constants.DIRECTION.LEFT) {
      icon = classnames(
        'expend-left pull-right',
        stateReview === constants.DIRECTION_STATE.NORMAL ? 'icon-right-ex' : 'icon-left-ex'
      );
    } else {
      icon = classnames(
        'expend-right pull-left',
        stateReview === constants.DIRECTION_STATE.NORMAL ? 'icon-left-ex' : 'icon-right-ex'
      );
    }

    const stateReviewMode =
      stateReview !== constants.DIRECTION_STATE.NORMAL
        ? constants.DIRECTION_STATE.NORMAL
        : direction === constants.DIRECTION.LEFT
          ? constants.DIRECTION_STATE.LEFT
          : constants.DIRECTION_STATE.RIGHT;
    const setStateReviewFunc = function() {
      setStateReview(stateReviewMode);
      reportPart.updateStateReview(stateReviewMode);
      // When direction != LEFT, call API to get new data
      store.needUpdatePreview(reportPart);
    };

    return (
      showPreview && (
        <a
          className={icon}
          title={
            stateReview !== 0
              ? constants.COLLAPSE_DEFAULT
              : direction === constants.DIRECTION.LEFT
                ? constants.EXPAND_RIGHT
                : constants.EXPAND_LEFT
          }
          onClick={setStateReviewFunc}
        />
      )
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/Izenda.ReportPartHeader.js